import React, { useCallback, useEffect, useRef, useState } from "react";
import "./RoutineGroup.scss";
import { BASE_URL } from "../../../utils/config";
import { adminRequest } from "../../../utils/requestMethods";
import { ToastContainer } from "react-toastify";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { IoIosAddCircle, IoIosMore } from "react-icons/io";
import CustomForm from "../../../components/CustomForm/CustomForm";
import OptionsMenu from "../../../components/OptionsMenu/OptionsMenu";
import NoRecord from "../../../components/NoRecord/NoRecord";

const RoutineGroup = () => {
  const filterForm = {
    course: "",
    semester: "",
    section: "",
    day: "",
    enrollmentSetupId: "",
    routinePeriodId: "",
  };
  const headers = [
    "Day",
    "Program",
    "Semester",
    "Section",
    "Enrollment",
    "Effective From",
    "Effective To",
  ];
  const [rows, setRows] = useState([]);
  const [formData, setFormData] = useState(filterForm);
  const [course, setCourse] = useState([]);
  const [semester, setSemester] = useState([]);
  const [sections, setSections] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [routinePeriod, setRoutinePeriod] = useState([]);
  const [visibleMenu, setVisibleMenu] = useState(null);
  const menuRefs = useRef([]);
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const fetchData = useCallback(async () => {
    try {
      const response = await adminRequest.post(
        `${BASE_URL}/routine/list/group`,
        {
          program: formData.course,
          semester: formData.semester,
          section: formData.section,
          day: formData.day,
          enrollmentSetupId: formData.enrollmentSetupId,
          routinePeriodId: formData.routinePeriodId,
        },
      );

      const fetchedRows = response.data.data.map((r) => ({
        displayData: [
          r.day,
          r.program.short_name,
          r.semester.semester,
          r.section.name,
          r.enrollmentSetup.enrollmentBatch,
          r.routinePeriod.effectiveFrom,
          r.routinePeriod.effectiveTo,
        ],
        day: r.day,
        eid: r.enrollmentSetup.id,
        rid: r.routinePeriod.id,
        pid: r.program.id,
        sid: r.semester.id,
        ccsmid: r.section.curriculumSectionMapId,
      }));
      setRows(fetchedRows);
    } catch (error) {
      console.error("Error fetching routines:", error);
      setRows([]);
    }
  }, [
    formData.course,
    formData.semester,
    formData.section,
    formData.day,
    formData.enrollmentSetupId,
    formData.routinePeriodId,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (formData.semester) {
      const fetchSection = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/section/list/by/curriculum`,
            {
              curriculumId: formData.semester,
            },
          );
          setSections(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSection();
    }
  }, [formData.semester]);

  useEffect(() => {
    const fetchEnrollment = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/enrollmentSetup/list`,
        );
        setAcademicYears(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchEnrollment();
  }, []);

  useEffect(() => {
    if (formData.enrollmentSetupId) {
      const fetchRoutinePeriod = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/routinePeriod/list/enrollmentSetupId`,
            {
              enrollmentSetupId: formData.enrollmentSetupId,
            },
          );
          setRoutinePeriod(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchRoutinePeriod();
    }
  }, [formData.enrollmentSetupId]);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/course/getNameAndId`,
        );
        setCourse(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchProgram();
  }, []);
  useEffect(() => {
    if (formData.course) {
      const fetchSemester = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/getRunningSemestersById`,
            { id: formData.course },
          );
          setSemester(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSemester();
    }
  }, [formData.course]);

  const handleCreateButtonClick = () => {
    navigate("/setup/routine/create");
  };

  const handleMoreClick = (rowIndex) => {
    setVisibleMenu(visibleMenu === rowIndex ? null : rowIndex);
  };

  const getMenuItems = (row) => [
    {
      link: `view/${row.day}/${row.eid}/${row.rid}/${row.pid}/${row.sid}/${row.ccsmid}`,
      text: "View",
    },
    {
      link: `edit/${row.day}/${row.eid}/${row.rid}/${row.pid}/${row.sid}/${row.ccsmid}`,
      text: "Edit",
    },
  ];

  const filterFields = [
    {
      name: "enrollmentSetupId",
      label: "Enrollment",
      type: "select",
      value: formData.enrollmentSetupId,
      onChange: handleChange,
      options: [
        { label: "Select Enrollment ", value: "" },
        ...academicYears.map((year) => ({
          label: year.enrollmentBatch,
          value: year.id,
        })),
      ],
    },
    {
      name: "routinePeriodId",
      label: "Routine Period",
      type: "select",
      value: formData.routinePeriodId,
      onChange: handleChange,
      options: [
        { label: "Select Routine Period", value: "" },
        ...routinePeriod.map((year) => ({
          label: `${year.effectiveFrom} to ${year.effectiveTo}`,
          value: year.id,
        })),
      ],
    },
    {
      name: "course",
      label: "Course",
      type: "select",
      value: formData.course,
      onChange: handleChange,
      options: [
        { label: "Select Course", value: "" },
        ...course.map((c) => ({
          label: c.short_name,
          value: c.id,
        })),
      ],
    },
    {
      name: "semester",
      label: "Semester",
      type: "select",
      value: formData.semester,
      onChange: handleChange,
      options: [
        { label: "Select Semester", value: "" },
        ...semester.map((s) => ({
          label: s.semester,
          value: s.id,
        })),
      ],
    },
    {
      name: "section",
      label: "Section",
      type: "select",
      value: formData.section,
      onChange: handleChange,
      options: [
        { label: "Select section", value: "" },
        ...sections.map((s) => ({
          label: s.Section.name,
          value: s.id,
        })),
      ],
    },
    {
      name: "day",
      label: "Day",
      type: "select",
      value: formData.day,
      onChange: handleChange,
      options: [
        { label: "Select Day", value: "" },
        ...[
          "SUNDAY",
          "MONDAY",
          "TUESDAY",
          "WEDNESDAY",
          "THURSDAY",
          "FRIDAY",
          "SATURDAY",
        ].map((day) => ({
          label: day.charAt(0) + day.slice(1).toLowerCase(),
          value: day,
        })),
      ],
    },
  ];

  return (
    <div className="routineListContainer">
      <div className="routineListContents">
        <div className="top">
          <span className="leftTitle">
            <FaArrowLeftLong onClick={handleBackClick} className="icon" />
            Routine Group
          </span>
          <span className="rightContents">
            <span className="createButton" onClick={handleCreateButtonClick}>
              <span className="title">Create Routine</span>
              <span className="addIcon">
                <IoIosAddCircle />
              </span>
            </span>
          </span>
        </div>
        <div className="filterForm">
          <CustomForm
            createButtonLabel="Search"
            flexDirection="row"
            showDefaultHeader={false}
            fields={filterFields}
            showCancelButton={false}
            showCloseButton={false}
            showCreateButton={false}
          />
        </div>
        <div className="bottom">
          <table className="listTable">
            <thead>
              <tr className="tableHeaderRow">
                <th></th>
                <th></th>
                {headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows && rows.length > 0 ? (
                rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td
                      style={{ position: "relative" }}
                      ref={(el) => (menuRefs.current[rowIndex] = el)}
                    >
                      <IoIosMore onClick={() => handleMoreClick(rowIndex)} />
                      {visibleMenu === rowIndex && (
                        <OptionsMenu
                          menuItems={getMenuItems(row)}
                          visible={true}
                        />
                      )}
                    </td>
                    <td></td>
                    {Array.isArray(row.displayData) &&
                      row.displayData.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={headers.length + 1}
                    style={{ fontSize: "14px", borderBottom: "none" }}
                  >
                    <NoRecord />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <ToastContainer position="top-center" />
      </div>
    </div>
  );
};

export default RoutineGroup;
